'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import dynamic from 'next/dynamic'

import { useTranslation } from '@/hooks/client/use-translation'
import { tw } from '@/lib/utils'

import type { PriceAlertProps, PriceAlertRelease } from './price-alert'

const PriceAlertDialog = dynamic<PriceAlertProps>(() => import('./price-alert'), { ssr: false })

const BellIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-amber-700" aria-hidden="true">
    <path
      d="M12 2C7.58 2 4 5.58 4 10v4.67L2.4 17.4A1 1 0 003.2 19h17.6a1 1 0 00.8-1.6L20 14.67V10c0-4.42-3.58-8-8-8z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M10 19a2.5 2.5 0 005 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="18" cy="5" r="3.5" fill="#ef4444" stroke="white" strokeWidth="1.5" />
  </svg>
)

/**
 * The amber "Never miss a price drop" banner and the price-alert dialog it
 * opens. The dialog is mounted on first open and unmounted 650ms after close so
 * its exit animation completes; it never loads for visitors who don't ask.
 */
export const PriceAlertBanner = ({
  release,
  variant = 'treatment',
}: {
  release: PriceAlertRelease
  variant?: 'control' | 'treatment'
}) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const unmountTimer = useRef<number | null>(null)

  const clearUnmountTimer = useCallback(() => {
    if (unmountTimer.current !== null) {
      window.clearTimeout(unmountTimer.current)
      unmountTimer.current = null
    }
  }, [])

  const openDialog = useCallback(() => {
    clearUnmountTimer()
    setMounted(true)
    setOpen(true)
  }, [clearUnmountTimer])

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (next) {
        openDialog()
        return
      }
      setOpen(false)
      clearUnmountTimer()
      unmountTimer.current = window.setTimeout(() => {
        setMounted(false)
        unmountTimer.current = null
      }, 650)
    },
    [clearUnmountTimer, openDialog]
  )

  useEffect(() => () => clearUnmountTimer(), [clearUnmountTimer])

  return (
    <div className={variant === 'control' ? 'mt-6' : 'mt-3'}>
      <div className="overflow-hidden rounded-2xl border border-amber-200/60 bg-gradient-to-b from-amber-50 via-white to-amber-50/30 shadow-sm">
        <div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-b from-amber-100 to-amber-200/60 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]">
              <BellIcon />
            </div>
            <div>
              <h3 className="text-navy m-0 text-base leading-tight font-bold">
                {t('product.neverMissPriceDrop', 'Never miss a price drop')}
              </h3>
              <p className="text-navy-60 mt-0.5 text-sm leading-snug">
                {t('product.getNotifiedPriceDrop', 'Get notified when prices drop for this product')}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={openDialog}
            data-tracking="ignore"
            className={tw(
              'focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-amber-700',
              variant === 'control'
                ? 'w-full shrink-0 cursor-pointer rounded-xl bg-gradient-to-b from-amber-400 to-amber-500 px-6 py-3 text-[17px] font-bold text-amber-950 shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_1px_3px_rgba(0,0,0,0.1)] transition-all hover:from-amber-500 hover:to-amber-600 hover:shadow-md active:scale-[0.97] sm:w-auto'
                : 'flex min-h-11 w-full shrink-0 cursor-pointer items-center justify-center self-end rounded-lg border border-amber-300 bg-amber-100 px-4 py-2 text-sm font-semibold text-amber-950 transition-colors hover:border-amber-400 hover:bg-amber-200 sm:w-auto sm:self-auto'
            )}
          >
            {t('client.product.setPriceAlert', 'Set Price Alert')}
          </button>
        </div>
      </div>
      {mounted ? (
        <PriceAlertDialog release={release} showTrigger={false} isOpen={open} onOpenChange={handleOpenChange} />
      ) : null}
    </div>
  )
}
